import { Text, tokens } from '@hotjar/hotsauce';

import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import { ListButton } from './ListButton';
import type { EventEmitter, NavigationProps } from '@hotjar/navigation/src/types';
import { Position } from '@hotjar/navigation/src/events/Position';

import { useNavigation } from '@hotjar/navigation/src/hooks/useNavigation';

export interface ProfileSidebarListProps {
  emitter: EventEmitter;
  isCollapsed: boolean;
  currentRouteConfig?: NavigationProps['currentRouteConfig'];
  sidebarNotifications?: NavigationProps['sidebarNotifications'];
  userName?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${tokens.spacer.x0};
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x0};
`;

export const ProfileSidebarList = ({
  emitter,
  isCollapsed,
  currentRouteConfig,
  sidebarNotifications,
  userName,
}: ProfileSidebarListProps): JSX.Element => {
  const { t } = useTranslation('navigation');
  const { routes, buildHrefFromRoute } = useNavigation();

  const profileSettingFlag = window.localStorage.getItem(
    'HJ_OVERRIDE_FEATURE:localization-profile-setting',
  );

  return (
    <Container>
      {!isCollapsed && (
        <Text
          textType="subtitle2"
          textColor="text_light"
          data-hj-suppress
          css={css`
            margin: ${tokens.spacer.x1} 0;
          `}
        >
          {userName || t('ProfileMenu.profile')}
        </Text>
      )}
      <List>
        <li>
          <ListButton
            text={t('ProfileMenu.myDetails')}
            icon="user_avatar"
            isCollapsed={isCollapsed}
            isActive={[
              routes.PROFILE.DETAILS.path,
              routes.PROFILE.EMAIL.path,
              routes.PROFILE.PASSWORD.path,
              routes.PROFILE.SIGN_IN_ACTIVITY.path,
            ].includes(currentRouteConfig?.path || '')}
            notificationLabel={sidebarNotifications?.myDetails}
            path={buildHrefFromRoute(routes.PROFILE.DETAILS.path)}
            onClick={() => {
              emitter.emit('clickedMyDetails', { position: Position.profileSettingsToolbar });
            }}
          />
        </li>
        {profileSettingFlag ? (
          <li>
            <ListButton
              text={t('ProfileMenu.language')}
              icon="translate"
              isCollapsed={isCollapsed}
              isActive={currentRouteConfig?.path?.startsWith(routes.PROFILE.LANGUAGE.path)}
              notificationLabel={sidebarNotifications?.language}
              path={buildHrefFromRoute(routes.PROFILE.LANGUAGE.path)}
              onClick={() => {
                emitter.emit('clickedLanguage', { position: Position.profileSettingsToolbar });
              }}
            />
          </li>
        ) : null}
        <li>
          <ListButton
            text={t('ProfileMenu.notifications')}
            icon="bell"
            isCollapsed={isCollapsed}
            isActive={currentRouteConfig?.path?.startsWith(routes.PROFILE.NOTIFICATIONS.path)}
            notificationLabel={sidebarNotifications?.notifications}
            path={buildHrefFromRoute(routes.PROFILE.NOTIFICATIONS.path)}
            onClick={() => {
              emitter.emit('clickedNotifications', { position: Position.profileSettingsToolbar });
            }}
          />
        </li>
        <li>
          <ListButton
            text={t('ProfileMenu.referAndEarn')}
            icon="gift"
            isCollapsed={isCollapsed}
            isActive={currentRouteConfig?.path?.startsWith(routes.PROFILE.REFERRALS.path)}
            notificationLabel={sidebarNotifications?.referAndEarn}
            path={buildHrefFromRoute(routes.PROFILE.REFERRALS.path)}
            onClick={() => {
              emitter.emit('clickedReferAndEarn', { position: Position.profileSettingsToolbar });
            }}
          />
        </li>
      </List>
    </Container>
  );
};
